import getWindow from 'get-window';

import {
    type Document,
    isTextRangeSelection,
    type NodeJSON,
    type NodeType,
    serialize,
    type Value,
} from '@gitbook/slate';
import { reportException } from '@gitbook/util-debug';

import { TRANSFER_TYPES } from '../constants';
import { encodeToBase64 } from './base64';
import findSlateDOMNode from './find-dom-node';
import findDOMRange from './find-dom-range';
import { ZERO_WIDTH_ATTRIBUTE, ZERO_WIDTH_SELECTOR } from './find-point';

const { FRAGMENT, HTML, TEXT } = TRANSFER_TYPES;

export interface ClipboardOptions {
    transform?: (node: NodeType, json: NodeJSON) => NodeJSON;
    getTransferData?: (fragment: Document) => { [dataProperty: string]: string };
}

/**
 * Decorate a clipboard event with a Slate document fragment and all other metadata required for rich copy/pasting.
 */
export function decorateClipboardEvent(
    event: ClipboardEvent,
    value: Value,
    options: ClipboardOptions = {}
) {
    if (!event.clipboardData?.setData) {
        // All modern browsers support the Clipboard API, but some very old browsers do not.
        throw new Error('Clipboard API not supported');
    }

    const win = getWindow(event.target);
    const native = win.getSelection();

    if (!native || native.rangeCount < 1) {
        reportException(new Error('Cannot copy a selection with no range'));
        return;
    }

    const content = getClipboardData(value, options, win);

    if (!content) {
        return;
    }

    event.preventDefault();

    for (const [contentType, data] of Object.entries(content)) {
        event.clipboardData.setData(contentType, data);
    }
}

/**
 * Copy the current selection to the clipboard, using the Clipboard API.
 */
export async function copySlateValueToClipboard(_value: Value, _options: ClipboardOptions = {}) {
    throw new Error('copySlateValueToClipboard is not implemented');
    // TODO: coming soon
    // if (!navigator.clipboard) {
    //     // All modern browsers support the Clipboard API, but some very old browsers do not.
    //     throw new Error('Clipboard API not supported');
    // }

    // const content = getClipboardData(value, options);
    // if (!content) {
    //     return;
    // }

    // const clipboardItem = new ClipboardItem(content);
    // await navigator.clipboard.write([clipboardItem]);
}

/*
 * Returns clipboard data for the current slection in Value. It relies on the Value being in the DOM.
 */
function getClipboardData(
    value: Value,
    options: ClipboardOptions,
    win: Window = window
):
    | {
          [contentType: string]: string;
      }
    | undefined {
    const { startKey, endKey } = value;
    const selection = value.selection;
    const isTextSelection = isTextRangeSelection(selection);
    const startVoid = startKey ? value.document.getClosestVoid(startKey) : null;

    // If the native selection is collapsed, and it isn't inside a void node, abort.
    if (isTextSelection && selection.isCollapsed && !startVoid) {
        return;
    }

    const fragment = value.fragment;

    // Turn the Slate selection into a native selection that represents the nodes we are copying.
    // This assumes the Slate selection is up-to-date at the moment of the copy.
    const range = findDOMRange(value.selection, win);

    if (!range) {
        return;
    }

    let contents = range.cloneContents();
    let attach = contents.childNodes[0];

    // Make sure attach is a non-empty node, since empty nodes will not get copied
    // COMPAT: ChildNodes is a NodeList and does not have .forEach on Edge,
    // so convert to an Array.
    Array.from(contents.childNodes).forEach((node) => {
        if (node.textContent && node.textContent.trim() !== '') {
            attach = node;
        }
    });

    const endVoid = endKey ? value.document.getClosestVoid(endKey) : null;
    // COMPAT: If the end node is a void node, we need to move the end of the
    // range from the void node's spacer span, to the end of the void node's
    // content, since the spacer is before void's content in the DOM.
    if (endVoid) {
        const r = range.cloneRange();
        const node = findSlateDOMNode(endVoid, win);

        if (node) {
            r.setEndAfter(node);
            contents = r.cloneContents();
        }
    }

    // COMPAT: If the start node is a void node, we need to attach the encoded
    // fragment to the void node's content node instead of the spacer, because
    // attaching it to empty `<div>/<span>` nodes will end up having it erased by
    // most browsers. (2018/04/27)
    if (startVoid) {
        const attachableChild = contents.childNodes?.[0]?.childNodes?.[1]?.firstChild;
        // Not all void nodes have a spacer, if there is no spacer
        // then this value might be null so we stick with the original attach node. (2022/12/13)
        if (attachableChild) {
            attach = attachableChild;
        }
    }

    // Remove any zero-width space spans from the cloned DOM so that they don't
    // show up elsewhere when pasted.
    [].slice.call(contents.querySelectorAll(ZERO_WIDTH_SELECTOR)).forEach((zw) => {
        const isNewline = zw.getAttribute(ZERO_WIDTH_ATTRIBUTE) === 'n';
        zw.textContent = isNewline ? '\n' : '';
    });

    // Remove any elements with data-exclude-from-copy attribute, used for editor-UI that should not be included in the copied selection
    [].slice.call(contents.querySelectorAll('[data-exclude-from-copy]')).forEach((node: Node) => {
        node.parentNode?.removeChild(node);
    });

    // If at this point we still have no attach node, then something is incorrect with the Slate/native selection.
    if (!attach) {
        throw new Error('clipboard: cannot find attach node');
    }

    // If the attach node is a text node, we need to wrap it in a span so that
    // we can set the `data-slate-fragment` attribute on it.
    const element: Element = (() => {
        if (attach.nodeType === Node.TEXT_NODE) {
            const span = win.document.createElement('span');

            // COMPAT: In Chrome and Safari, if we don't add the `white-space` style
            // then leading and trailing spaces will be ignored. (2017/09/21)
            span.style.whiteSpace = 'pre';

            // cloning the attach node because if we use the attach node directly
            // it breaks the selection for BlockRange selections
            isTextSelection ? span.appendChild(attach) : span.appendChild(attach.cloneNode(true));

            contents.appendChild(span);
            return span;
        }

        return attach as Element;
    })();

    const serialized = serialize(fragment, {
        preserveKeys: true,
        transform: options?.transform,
    });
    const encoded = encodeToBase64(serialized);
    const otherContent = options?.getTransferData?.(fragment) ?? {};
    // Set a `data-slate-fragment` attribute on a non-empty node, so it shows up
    // in the HTML, and can be used for intra-Slate pasting. If it's a text
    // node, wrap it in a `<span>` so we have something to set an attribute on.
    element.setAttribute('data-slate-fragment', encoded);

    // text/plain representation:
    const plainText = fragment.getText('\n');

    // Add the phony content to a div element. This is needed to copy the
    // contents into the html clipboard register.
    const div = win.document.createElement('div');
    div.appendChild(contents);

    return {
        [TEXT]: plainText,
        [HTML]: div.innerHTML,
        [FRAGMENT]: encoded,
        ...otherContent,
    };
}
